<template>
  <div class="d-flex">
    <v-card
      class="rounded-lg elevation-0"
    >
      <v-list dense>
        <v-subheader>
          SELECTED POINTS
          <v-spacer />
          <v-btn
            icon
            small
            class="ml-1"
            :color="log ? 'primary' : undefined"
            :outlined="log"
            @click="toggleLog"
          >
            <v-icon small>
              {{ mdiMathLog }}
            </v-icon>
          </v-btn>
          <v-btn
            icon
            small
            @click="close"
          >
            <v-icon small>
              {{ mdiClose }}
            </v-icon>
          </v-btn>
        </v-subheader>
        <v-list-item-group
          v-model="selectedMarkerIndex"
          color="primary"
          mandatory
          @change="selectMarker"
        >
          <v-list-item
            v-for="marker in currentMarkers"
            :key="marker._leaflet_id"
          >
            <v-list-item-content>
              <v-list-item-title>
                {{ marker.options.data.name }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list-item-group>
      </v-list>
    </v-card>
    <v-card v-show="showMenu" class="ml-2 rounded-lg elevation-0 align-self-start">
      <v-list class="py-0" dense>
        <v-hover
          v-for="item in items"
          :key="item.title"
          v-slot="{ hover }"
        >
          <v-list-item
            :two-line="item.twoLine"
            @click="() => item.click(selectedMarker, item.formInfo)"
          >
            <v-list-item-content>
              <v-list-item-title>{{ item.title }}</v-list-item-title>
              <v-list-item-subtitle v-if="item.twoLine">{{ selectedMarker.options.data[item.subtitleKey] }}</v-list-item-subtitle>
            </v-list-item-content>
            <template v-if="item.edit && hover">
              <v-list-item-icon
                class="align-self-center"
              >
                <v-btn
                  small
                  icon
                  @click.stop="() => item.subclick(selectedMarker, item.formInfo)"
                >
                  <v-icon> {{ mdiCircleEditOutline }} </v-icon>
                </v-btn>
              </v-list-item-icon>
            </template>
            <template v-else>
              <v-list-item-icon class="align-self-center">
                <v-icon> {{ item.icon }} </v-icon>
              </v-list-item-icon>
            </template>
          </v-list-item>
        </v-hover>
      </v-list>
      <div v-if="log && selectedMarker" class="px-2 pb-2">
        <v-divider />
        <p class="ma-0">Local X: {{ selectedMarker.options.data.x }}</p>
        <p class="ma-0">Local Y: {{ selectedMarker.options.data.y }}</p>
        <p class="ma-0">
          Game X: {{ selectedMarker.options.data.gameX }}
        </p>
        <p class="ma-0">
          Game Y: {{ selectedMarker.options.data.gameY }}
        </p>
      </div>
    </v-card>
  </div>
</template>

<script>
import L from 'leaflet'
import { mdiClose, mdiMathLog } from '@mdi/js'
import { mdiCircleEditOutline } from '@mdi/js';
export default {
  props: {
    map: {
      type: Object,
      required: true
    },
    markers: {
      type: Array,
      required: true
    },
    setInitMarker: {
      type: Function,
      required: true
    },
    closeControl: {
      type: Function,
      required: true
    }
  },
  data () {
    return {
      mdiClose,
      mdiMathLog,
      mdiCircleEditOutline,
      showMenu: false,
      selectedMarkerIndex: null,
      currentMarkers: this.markers,
      items: [],
      log: false
    }
  },
  computed: {
    selectedMarker () {
      return this.currentMarkers[this.selectedMarkerIndex]
    }
  },
  mounted () {
    this.map.on('marker-removed', this.removeMarker)
  },
  beforeDestroy () {
    this.map.off('marker-removed', this.removeMarker)
  },
  methods: {
    getSelectedMarker () {
      return this.selectedMarker
    },
    updateMarkers (markers) {
      this.currentMarkers = markers
    },
    setSelectedMarkerIndex (index) {
      this.selectedMarkerIndex = index
      this.selectMarker(index)
    },
    selectMarker (val) {
      const marker = this.currentMarkers[val]
      if (marker) {
        this.map.eachLayer(layer => {
          if (layer instanceof L.Marker && layer !== marker) {
            layer.setInteractive(false)
            layer.setOpacity(0.3)
            layer.getElement().style.setProperty('--pseudo-backgroundcolor', layer.options.inactiveColor)
          } else if (layer instanceof L.Marker) {
            layer.setInteractive(true)
            this.setInitMarker(layer)
            this.items = layer.options.items
            layer.setOpacity(1)
            layer.getElement().style.setProperty('--pseudo-backgroundcolor', layer.options.defaultColor)
          }
        })
        this.showMenu = true
      } else {
        this.showMenu = false
        this.items = []
        this.map.eachLayer(layer => {
          if (layer instanceof L.Marker) {
            layer.setInteractive(true)
            layer.setOpacity(1)
            layer.getElement().style.setProperty('--pseudo-backgroundcolor', layer.options.defaultColor)
          }
        })
      }
    },
    removeMarker (marker) {
      this.currentMarkers = this.currentMarkers.filter(m => m._leaflet_id !== marker._leaflet_id)

      if (this.currentMarkers.length === 0) {
        return this.close()
      } else {
        this.setInitMarker(this.currentMarkers[0])
      }

      if (marker === this.selectedMarker) {
        this.showMenu = false
        this.map.eachLayer(layer => {
          if (layer instanceof L.Marker) {
            layer.setInteractive(true)
            layer.setOpacity(1)
            layer.getElement().style.setProperty('--pseudo-backgroundcolor', layer.options.defaultColor)
          }
        })
      }
    },
    close () {
      this.showMenu = false
      this.items = []
      this.map.eachLayer(layer => {
        if (layer instanceof L.Marker) {
          layer.setInteractive(true)
          layer.setOpacity(1)
          layer.getElement().style.setProperty('--pseudo-backgroundcolor', layer.options.defaultColor)
        }
      })
      this.closeControl()
    },
    toggleLog () {
      this.log = !this.log
    }
  }
}
</script>

<style scoped>
  .v-list-item--dense .v-list-item__icon,
  .v-list--dense .v-list-item .v-list-item__icon {
    margin-top: 0px;
    margin-bottom: 0px;
  }

  .v-list-item--dense.v-list-item--two-line,
  .v-list--dense .v-list-item.v-list-item--two-line,
  .v-list-item--dense,
  .v-list--dense .v-list-item {
    min-height: auto;
  }
</style>
